import { z } from "zod";

// Phase 6 typed configSchema for the `summary-checklist` template (D-03..D-07).
// Replaces the Phase 01.1 z.unknown() placeholder in TEMPLATE_CATALOG; validateRecipe
// Pass 2A now enforces shape end-to-end.
//
// `VisibleWhen` (D-04) was the MINIMAL Phase-6 predicate shape that Plan 08's
// renderer consumed via `apps/web/src/widgets/summary-checklist/visible-when.ts`.
// Phase 9 Plan 09-05 D-16 GENERALIZED visibility into the full visibility
// Constraint AST entry in `recipe.constraints[]`, replacing inline `visibleWhen`
// with `visibilityConstraintId` on each item. `VisibleWhen` + `VisibleWhenSchema`
// EXPORTS ARE PRESERVED because:
//   1. The migration script in `packages/shared/scripts/migrate-visibleWhen-to-constraint.ts`
//      reads pre-migration recipes carrying the legacy shape during rewrite.
//   2. The OptionCategory schema (Phase 8 D-15 AUTHR-02) still carries an inline
//      visibleWhen of the same shape — that surface is generalized in a later
//      Phase 9 plan, not 09-05.
// Author UI consumers of visibility-on-checklist-items must migrate to author a
// recipe-level visibility Constraint and reference it via `visibilityConstraintId`.
//
// z.discriminatedUnion("type", [...]) requires each variant to be a plain
// z.strictObject with z.literal(...) as the discriminator (Zod 4.4.3 semantics).
// Cross-reference: packages/shared/src/schemas/option-category.ts file header for
// the broader discriminatedUnion explainer and the contrast against constraint.ts
// where z.union is needed because variants are themselves unions.
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes
// (Phase 01.1 D-10/D-15): every exported type is an explicit interface; every
// exported schema const carries a `z.ZodType<Interface>` annotation so tsc can
// emit declarations without reading across module boundaries (no TS9013).

// ---------------------------------------------------------------------------
// VisibleWhen — D-04 minimal predicate (Phase 9 generalizes per D-16)
// ---------------------------------------------------------------------------
// PRESERVED for the migration script + OptionCategory consumers; NOT used on
// SummaryChecklistItem anymore.

export interface VisibleWhen {
  sourceWidgetId: string;
  anyOf: ReadonlyArray<string>;
}

const _visibleWhenSchema = z.strictObject({
  // Accepts both kebab-case widget instance ids and uuids per Phase 6 transitional
  // contract; cross-recipe instance-id validation lives in Pass 2A's outer loop.
  sourceWidgetId: z.string().min(1),
  // anyOf with zero options would always evaluate false (hide always) — meaningless.
  anyOf: z.array(z.string().min(1)).min(1),
});

export const VisibleWhenSchema: z.ZodType<VisibleWhen> = _visibleWhenSchema;

// ---------------------------------------------------------------------------
// SummaryChecklistItem — D-04, D-06 (id, title, optional fields)
// Phase 9 Plan 09-05 D-16 — `visibleWhen` REMOVED; `visibilityConstraintId`
// ADDED. The new field is a kebab-case reference (≤100 chars) resolved against
// `recipe.constraints[]` at the document-position id `constraint-${index}`
// (Q-02 resolution). Migration is performed at API boot by
// `migrateVisibleWhenToConstraint(db)`.
// ---------------------------------------------------------------------------

export interface SummaryChecklistItem {
  id: string;
  title: string;
  description?: string | undefined;
  subItems?: ReadonlyArray<{ id: string; title: string }> | undefined;
  groupId?: string | undefined;
  visibilityConstraintId?: string | undefined;
}

const _subItemSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  title: z.string().min(1).max(200),
});

const _itemSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(1000).optional(),
  subItems: z.array(_subItemSchema).optional(),
  // References recipe-level OptionCategory ids per Phase 01.1 D-16..D-19.
  groupId: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required")
    .optional(),
  // Phase 9 Plan 09-05 D-16 — reference into recipe.constraints[] (visibility
  // kind). Document-position id scheme: `constraint-${index}` (Q-02). The
  // renderer-side helper `isVisibleById` resolves the constraint, evaluates
  // its `when` clause, and falls back to "visible" if unresolved/non-visibility
  // (Plan 09-PATTERNS Pitfall 7 default-true).
  visibilityConstraintId: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required")
    .optional(),
});

// ---------------------------------------------------------------------------
// SummaryChecklistSource — D-05 discriminated union
// ---------------------------------------------------------------------------

export type SummaryChecklistSource =
  | { type: "widget"; sourceWidgetId: string }
  | { type: "selector"; pathInState: string }
  | { type: "static" };

const _widgetSourceSchema = z.strictObject({
  type: z.literal("widget"),
  sourceWidgetId: z.string().min(1),
});

const _selectorSourceSchema = z.strictObject({
  type: z.literal("selector"),
  pathInState: z.string().min(1),
});

const _staticSourceSchema = z.strictObject({
  type: z.literal("static"),
});

const _sourceUnion = z.discriminatedUnion("type", [
  _widgetSourceSchema,
  _selectorSourceSchema,
  _staticSourceSchema,
]);

export const SummaryChecklistSourceSchema: z.ZodType<SummaryChecklistSource> = _sourceUnion;

// ---------------------------------------------------------------------------
// SummaryChecklistConfig — D-06 root config
// ---------------------------------------------------------------------------

export interface SummaryChecklistConfig {
  items: ReadonlyArray<SummaryChecklistItem>;
  source: SummaryChecklistSource;
  viewMode: "title" | "snippet" | "full" | "expanded";
  groupBy: "category" | "none";
}

export const SummaryChecklistConfigSchema: z.ZodType<SummaryChecklistConfig> = z.strictObject({
  items: z.array(_itemSchema).min(0),
  source: _sourceUnion,
  viewMode: z.enum(["title", "snippet", "full", "expanded"]).default("title"),
  groupBy: z.enum(["category", "none"]).default("none"),
});
